import { useState } from 'react'
import o from '../../components/Overlay.module.css'
import styles from './PersonRecordPanel.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson, ApiError } from '../../lib/api'
import { dmy, money, plural } from '../wages/format'
import { PendingButton } from '../../components/pending'
import { DateField } from '../../components/DateField'

// Correcting a past rate (D6.3b option (a), locked-day rule (i); product
// approved, payroll-owner confirmation outstanding). The server half is
// apps/api/src/services/rate-correction.js. Nothing is saved until the person
// has seen which dates change; the server then checks them again.

export interface RateCorrectionEntry {
  id: string
  corrected_at: string
  corrected_by_email: string | null
  interval_from: string
  interval_to: string | null
  new_amount: number | string
  reason: string | null
  before_rows: { amount: number | string; effective_from: string; effective_to: string | null }[]
  after_rows: { amount: number | string; effective_from: string; effective_to: string | null }[]
  requeued_days: number
}

interface Preview {
  token: string
  affected: { amount: string; effective_from: string; effective_to: string | null }[]
  result: { amount: string; effective_from: string; effective_to: string | null; corrected: boolean }[]
  corrected_from: string
  corrected_to: string | null
  worked_days: number
  locked_dates: string[]
  can_save: boolean
}

const span = (from: string, to: string | null) => (to ? `${dmy(from)} – ${dmy(to)}` : `${dmy(from)} onwards`)
const stamp = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit' })}`
}

export function RateCorrection({
  personId,
  name,
  token,
  corrections,
  mayCorrect,
  onChanged,
}: {
  personId: string
  name: string
  token: string | undefined
  corrections: RateCorrectionEntry[]
  mayCorrect: boolean
  onChanged: () => void
}) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [busy, setBusy] = useState<'preview' | 'save' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lockedRefusal, setLockedRefusal] = useState<string[] | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const amountNum = Number(amount.replace(/[$,\s]/g, ''))
  const amountOk = amount.trim() !== '' && Number.isFinite(amountNum) && amountNum > 0
  const body = { from, to: to || null, amount: amountNum, reason: reason.trim() || undefined }
  const ready = !!from && amountOk && (!to || to >= from)

  const fail = (e: unknown) => {
    const body = e instanceof ApiError ? (e.body as { error?: string; code?: string; locked_dates?: string[] } | null) : null
    setLockedRefusal(body?.code === 'DAY_LOCKED' ? body.locked_dates || [] : null)
    setError(body?.error || (e as Error)?.message || 'That did not work. Nothing was saved.')
    if (body?.code === 'RATE_HISTORY_CHANGED') onChanged()
  }
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v)
    setPreview(null)
    setError(null)
    setLockedRefusal(null)
  }

  const check = async () => {
    setBusy('preview')
    setError(null)
    setLockedRefusal(null)
    try {
      setPreview(await apiJson<Preview>(user, `/api/personnel/${personId}/rate-corrections/preview`, { method: 'POST', body }))
    } catch (e) {
      setPreview(null)
      fail(e)
    } finally {
      setBusy(null)
    }
  }
  const save = async () => {
    if (!preview || !token) return
    setBusy('save')
    setError(null)
    try {
      const r = await apiJson<{ requeued_days: number }>(user, `/api/personnel/${personId}/rate-corrections`, { method: 'POST', body: { ...body, expectedToken: token } })
      setDone(`Rate corrected. ${plural(r.requeued_days, 'worked day', 'worked days')} queued to be recalculated.`)
      setOpen(false)
      setPreview(null)
      setFrom('')
      setTo('')
      setAmount('')
      setReason('')
      onChanged()
    } catch (e) {
      fail(e)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={styles.corrections} data-testid="rate-corrections">
      <div className={styles.subHead}>
        <span className={styles.subTitle}>Rate corrections</span>
      </div>
      <span className={o.fieldHint}>
        A correction replaces the rate for the dates you choose, on a rate that was already recorded. Rates outside those dates stay as they were, and the old rate and dates are kept below.
        Wages for {name}&rsquo;s worked days in those dates are recalculated. A day that is locked is never recalculated, so a correction that reaches one is refused as a whole and nothing is saved. A
        locked day only means CONTROL will no longer change it. It does not prove what was paid, and neither does a stored rate: the payroll system is the authority on what was paid.
      </span>
      {done && (
        <div className={styles.next} role="status">
          {done}
        </div>
      )}
      {mayCorrect && !open && (
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => {
            setOpen(true)
            setDone(null)
          }}
        >
          Correct a past rate
        </button>
      )}
      {mayCorrect && open && (
        <div className={styles.correctForm} role="group" aria-label="Correct a past rate">
          <div className={o.grid2}>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="rc-from">
                Correct from
              </label>
              <DateField id="rc-from" className={o.input} value={from} onChange={(e) => edit(setFrom)(e.target.value)} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="rc-to">
                Correct to <span className={styles.optional}>optional</span>
              </label>
              <DateField id="rc-to" className={o.input} value={to} onChange={(e) => edit(setTo)(e.target.value)} />
              <span className={o.fieldHint}>Leave empty to correct through the end of the current rate.</span>
            </div>
          </div>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="rc-amount">
              Correct rate
            </label>
            <input id="rc-amount" inputMode="decimal" className={o.input} value={amount} placeholder="$ per hour" onChange={(e) => edit(setAmount)(e.target.value)} />
          </div>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="rc-reason">
              Reason <span className={styles.optional}>optional</span>
            </label>
            <input id="rc-reason" className={o.input} value={reason} maxLength={500} onChange={(e) => edit(setReason)(e.target.value)} />
          </div>
          {to && to < from && <span className={styles.needed}>The end date cannot be before the start date.</span>}

          {preview && (
            <div className={styles.preview} data-testid="correction-preview">
              <div>
                <strong>
                  {span(preview.corrected_from, preview.corrected_to)} changes
                </strong>{' '}
                from {preview.affected.map((a) => `${money(Number(a.amount))}/h`).filter((v, i, all) => all.indexOf(v) === i).join(' and ')} to {money(amountNum)}/h.
              </div>
              <div className={styles.versions}>
                {preview.result.map((r, i) => (
                  <div key={i} className={styles.version}>
                    <span className={styles.versionAmt}>{money(Number(r.amount))}/h</span>
                    <span className={styles.versionSpan}>{span(r.effective_from, r.effective_to)}</span>
                    <span className={styles.locked}>{r.corrected ? 'Corrected' : 'Unchanged'}</span>
                  </div>
                ))}
              </div>
              {preview.locked_dates.length > 0 ? (
                <div className={`${o.warn} ${styles.block}`} role="alert">
                  Cannot save. {plural(preview.locked_dates.length, 'locked day', 'locked days')} would change: {preview.locked_dates.map(dmy).join(', ')}. Locked days are never recalculated, so
                  choose dates that do not include them. Unlocking is a separate step and is not done here.
                </div>
              ) : (
                <div className={styles.next}>
                  {preview.worked_days === 0
                    ? `${name} has no worked days in these dates, so no wages are recalculated.`
                    : `${plural(preview.worked_days, 'worked day', 'worked days')} will be queued to recalculate. Earlier exports are kept as they were, and no payment difference is calculated.`}
                </div>
              )}
            </div>
          )}
          {error && (
            <div className={`${o.warn} ${styles.block}`} role="alert">
              {error}
              {lockedRefusal && lockedRefusal.length > 0 && <> Locked: {lockedRefusal.map(dmy).join(', ')}.</>}
            </div>
          )}
          <div className={styles.correctActions}>
            <PendingButton type="button" className={styles.smallGhost} busy={busy === 'preview'} pending="Checking…" disabled={!ready || busy !== null} onClick={check}>
              Show affected dates
            </PendingButton>
            <PendingButton type="button" className={styles.smallPrimary} busy={busy === 'save'} pending="Saving…" disabled={!preview || !preview.can_save || !token || busy !== null} onClick={save}>
              Save correction
            </PendingButton>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => {
                setOpen(false)
                setPreview(null)
                setError(null)
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {corrections.length > 0 && (
        <div className={styles.versions} data-testid="correction-history">
          {corrections.map((c) => (
            <div key={c.id} className={styles.correctionEntry} data-testid="correction-entry">
              <div>
                <strong>{span(c.interval_from, c.interval_to)}</strong> corrected to {money(Number(c.new_amount))}/h
              </div>
              <div className={styles.next}>
                Was {c.before_rows.map((r) => `${money(Number(r.amount))}/h for ${span(r.effective_from, r.effective_to)}`).join('; ')}
              </div>
              <div className={styles.next}>
                {stamp(c.corrected_at)}
                {c.corrected_by_email ? ` by ${c.corrected_by_email}` : ''}. {plural(c.requeued_days, 'worked day', 'worked days')} queued to recalculate.
                {c.reason ? ` Reason: ${c.reason}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
